import { makeObservable, observable, computed, action, flow } from 'mobx';
import { generateSearchQuery } from '@common/utils';
import {
  IODataSearchResult,
  IOrderByParams,
  ISearchParams,
  ISearchResponse,
  SearchResponse,
} from '@common/models';
import { SearchService } from '@common/services/searchService';
import { SortOrder, TableColumn } from 'react-data-table-component';
import {
  IQueryCacheStoreInit,
  QueryCacheStore,
} from '@common/stores/QueryCacheStore';

export interface SearchStoreInit<TSearchFilter> {
  endpointUrl: SearchService.SearchEndPoints;
  defaultSearchParams: Partial<ISearchParams>;
  defaultSearchFilter: TSearchFilter;
}

/**
 * A reusable search store, the SearchService uses the same pattern for all searches, this implementation
 * is intended to be used anywhere we need to use the SearchService.
 */
export abstract class SearchStore<
  TSearchFilter,
  TSearchResultModelInterface,
  TSearchResultModelClass extends Record<
    keyof TSearchResultModelInterface,
    any
  >,
  TSearchResult,
> extends QueryCacheStore {
  endpointUrl: SearchService.SearchEndPoints;
  defaultSearchParams!: ISearchParams;
  defaultSearchFilter!: TSearchFilter;
  mapSearchResults: (
    results: TSearchResult,
  ) => SearchResponse<TSearchResultModelClass>;
  @observable searchFilter!: TSearchFilter;
  @observable searchParams!: ISearchParams;
  @observable isLoading: boolean = false;
  @observable searchResults: SearchResponse<TSearchResultModelClass> =
    new SearchResponse<TSearchResultModelClass>({
      results: [],
      totalRecordCount: 0,
    });

  /**
   * Returns true if the search results indicates there is more than 1 page of results to display for the current filter and params.
   */
  hasMultiplePagesImpl(): boolean {
    return (
      this.searchResults.totalRecordCount > 0 &&
      this.searchResults.totalRecordCount > this.searchParams.top
    );
  }

  protected abstract combinedSearchParamsImpl(): ISearchParams;

  /**
   *
   * @param entityName The name of the entity you are searching, i.e. stores, organizations, users, zipCode etc, Refer to SearchService.cs in PetPass Solution...
   * @param defaultOrderBy An Array<IOrderByParams> indicated the default order in which to sort search results.
   * @param defaultSearchFilter The default searchFilter, this is used to reset search, Generic Type SearchFilter indicates the type of defaultSearchFilter
   * @param searchFilter A reference to the active searchFilter based on Generic Type SearchFilter.
   */
  constructor(
    init: SearchStoreInit<TSearchFilter>,
    mapSearchResults: (
      results: TSearchResult,
    ) => SearchResponse<TSearchResultModelClass>,
    queryClientInit?: IQueryCacheStoreInit,
  ) {
    super(queryClientInit);

    if (!init.endpointUrl)
      throw 'SearchStore requires the endpointUrl field to be specified';
    if (!init.defaultSearchFilter)
      throw 'SearchStore requires the defaultSearchFilter field to be specified';
    if (!mapSearchResults)
      throw 'SearchStore requires a mapSearchResults callback to be specified in order to know how to instantiate search results';

    this.endpointUrl = init.endpointUrl;
    this.mapSearchResults = mapSearchResults;
    this.defaultSearchParams = {
      skip: 0,
      top: 10,
      orderby: [],
      search: undefined,
      ...init.defaultSearchParams,
    };

    if (this.defaultSearchParams.orderby.length === 0) {
      this.defaultSearchParams.orderby = [{ field: 'name', dir: 'asc' }];
    }

    this.searchParams = { ...this.defaultSearchParams };
    this.defaultSearchFilter = { ...init.defaultSearchFilter };
    this.searchFilter = { ...this.defaultSearchFilter };

    makeObservable(this, undefined, { autoBind: true });
  }

  @computed get combinedSearchParams(): ISearchParams {
    return this.combinedSearchParamsImpl();
  }

  /**
   * Allows updating the default search filter after the class instantiation to support adding more default after appStore has loaded configuration if the config is needed for a defaultFilter value.
   * @param searchParams
   */
  @action.bound setPartialDefaultSearchFilter(
    searchFilter: Partial<TSearchFilter>,
  ) {
    this.defaultSearchFilter = { ...this.defaultSearchFilter, ...searchFilter };
    const currentSearchFilter = this.searchFilter;
    this.searchFilter = { ...currentSearchFilter, ...this.defaultSearchFilter };
  }

  @computed get hasMultiplePages() {
    return this.hasMultiplePagesImpl();
  }

  /** Use this to get the current page the search store is on */
  @computed get currentPage() {
    return this.searchParams.skip === 0
      ? 1
      : Math.ceil(
          _.defaultTo(this.searchParams.top, this.defaultSearchParams.top) /
            _.defaultTo(this.searchParams.skip, this.defaultSearchParams.skip),
        ) + 1;
  }

  /**
   * If there are multiple results, call this to advance to a specific page of results (0 index based)
   * @param newPage The page number you wish to show results for.
   */
  @action.bound changePage(newPage: number) {
    this.searchParams = {
      ...this.searchParams,
      skip: this.searchParams.top * newPage,
    };
  }

  /**
   * designed to be passed to a DataTable components sort prop, handles updating search params for you from DataTable sort.
   */
  @action.bound updateRemoteDTSort(
    selectedColumn: TableColumn<TSearchResultModelClass>,
    sortDirection: SortOrder,
  ) {
    if (!selectedColumn.sortField) return;

    let orderby: Array<IOrderByParams> = [
      {
        field: selectedColumn.sortField,
        dir: sortDirection,
      },
    ];
    this.updateSearchParams({ orderby });
  }

  /**
   * Applies your partial search params object to the searchParam object which causes the computed for combinedSearchParams to update.
   * to rerun searches use a useEffect with deps on combinedSearchParams, when combinedSearchParams changes rerun calls to search.
   */
  @action.bound updateSearchParams(searchParams: Partial<ISearchParams>) {
    this.searchParams = { ...this.searchParams, ...searchParams };
  }

  /**
   * Applies your partial search filter object to the searchFilter object which causes the computed for combinedSearchParams to update.
   */
  @action.bound updateSearchFilter(searchFilter: Partial<TSearchFilter>) {
    this.searchParams = { ...this.searchParams, skip: 0 };
    this.searchFilter = { ...this.searchFilter, ...searchFilter };
  }

  /**
   * Reset the search filter and paging to the defaults from instantiation.
   */
  @action.bound resetSearchFilter() {
    this.searchFilter = { ...this.defaultSearchFilter };
    this.searchParams = { ...this.defaultSearchParams }; //spread so we get a new item and not a reference to a default item.
  }

  /**
   * Reset the search filter and paging to the defaults from instantiation.
   */
  @action.bound resetSearchResults() {
    this.searchResults = new SearchResponse<TSearchResultModelClass>({
      results: [],
      totalRecordCount: 0,
    });
  }

  /**
   * Execute the search and populate the results in searchResults
   */
  search = flow(function* (
    this: SearchStore<
      TSearchFilter,
      TSearchResultModelInterface,
      TSearchResultModelClass,
      TSearchResult
    >,
  ) {
    this.isLoading = true;

    try {
      if (this.queryClient === undefined) {
        this.searchResults = yield SearchService.search<
          TSearchResultModelInterface,
          TSearchResultModelClass,
          TSearchResult
        >(this.endpointUrl, this.combinedSearchParams, this.mapSearchResults);
      } else {
        this.searchResults = yield this.queryClient.fetchQuery({
          queryKey: [this.endpointUrl, this.combinedSearchParams],
          queryFn: () =>
            SearchService.search<
              TSearchResultModelInterface,
              TSearchResultModelClass,
              TSearchResult
            >(
              this.endpointUrl,
              this.combinedSearchParams,
              this.mapSearchResults,
            ),
          staleTime: this.queryStaleTime ?? 300000,
        });
      }
    } finally {
      this.isLoading = false;
    }
  });

  fakeSearch = flow(function* (
    this: SearchStore<
      TSearchFilter,
      TSearchResultModelInterface,
      TSearchResultModelClass,
      TSearchResult
    >,
  ) {
    this.isLoading = true;
    this.searchResults = yield {
      results: [],
      totalRecordCount: 0,
    };
    this.isLoading = false;
  });
}

export class StandardSearchStore<
  TSearchFilter,
  TModelInterface,
  TModel extends Record<keyof TModelInterface, any>,
> extends SearchStore<
  TSearchFilter,
  TModelInterface,
  TModel,
  ISearchResponse<TModelInterface>
> {
  constructor(
    init: SearchStoreInit<TSearchFilter>,
    mapSearchResult: (item: TModelInterface) => TModel,
    queryClientInit?: IQueryCacheStoreInit,
  ) {
    super(
      init,
      (results: ISearchResponse<TModelInterface>) => {
        return new SearchResponse<TModel>({
          results: results.results.map(mapSearchResult),
          totalRecordCount: results.totalRecordCount,
        });
      },
      queryClientInit,
    );
  }

  combinedSearchParamsImpl(): ISearchParams {
    return {
      ...this.searchParams,
      search: generateSearchQuery(this.searchFilter),
    };
  }
}
export class ODataSearchStore<
  TSearchFilter,
  TModelInterface,
  TModel extends Record<keyof TModelInterface, any>,
> extends SearchStore<
  TSearchFilter,
  TModelInterface,
  TModel,
  IODataSearchResult<TModelInterface>
> {
  combinedSearchParamsImpl(): ISearchParams {
    return { ...this.searchParams, ...this.searchFilter };
  }
}
